<div
  #dialogEl
  class="pd"
  role="dialog"
  aria-modal="true"
  aria-labelledby="pd-title"
  aria-describedby="pd-desc"
>
  <header class="pd__header">
    <div>
      <h2 id="pd-title">{{ title }}</h2>
      <p id="pd-desc" class="pd__sub" *ngIf="step === 'number'">
        <ng-container *ngIf="mode === 'change' && currentMasked">
          Current number: <strong>{{ currentMasked }}</strong>.
          We'll send a code to the new number to confirm it's yours.
        </ng-container>
        <ng-container *ngIf="!(mode === 'change' && currentMasked)">
          Your personal coach messages you on WhatsApp. We'll send a 6-digit code to confirm it's your number.
        </ng-container>
      </p>
      <p id="pd-desc" class="pd__sub" *ngIf="step === 'code'">
        We sent a 6-digit code on WhatsApp to <strong>{{ maskedNumber }}</strong>.
      </p>
      <p id="pd-desc" class="pd__sub" *ngIf="step === 'done'">Your coach can now reach you on WhatsApp.</p>
    </div>
    <button
      type="button"
      class="pd__close"
      aria-label="Close"
      [disabled]="sending"
      (click)="cancel()"
      *ngIf="step !== 'done'"
    >
      <i class="bi bi-x-lg" aria-hidden="true"></i>
    </button>
  </header>

  <!-- Step 1: country + number + consent -->
  <form class="pd__body" *ngIf="step === 'number'" (ngSubmit)="sendCode()" novalidate>
    <div class="pd__field">
      <label for="pd-number">WhatsApp number</label>
      <div class="phone-row">
        <div class="country-picker" [class.is-open]="dropdownOpen">
          <button
            type="button"
            class="country-picker__trigger"
            (click)="toggleDropdown()"
            [attr.aria-expanded]="dropdownOpen"
            aria-haspopup="listbox"
            [attr.aria-label]="'Country: ' + selectedCountry.name + ' ' + selectedCountry.dialCode"
          >
            <span class="country-picker__iso">{{ selectedCountry.iso2 }}</span>
            <span class="country-picker__dial">{{ selectedCountry.dialCode }}</span>
            <span class="country-picker__caret" aria-hidden="true"></span>
          </button>
          <div class="country-picker__panel" *ngIf="dropdownOpen">
            <input
              #searchInput
              type="search"
              class="country-picker__search"
              placeholder="Search country or code"
              aria-label="Search countries"
              [ngModel]="searchQuery"
              (ngModelChange)="searchQuery = $event"
              name="countrySearch"
            />
            <ul class="country-picker__list" role="listbox" aria-label="Countries">
              <li *ngIf="filteredCountries.length === 0" class="country-picker__empty">No matches</li>
              <li *ngFor="let c of filteredCountries" role="presentation">
                <button
                  type="button"
                  role="option"
                  class="country-picker__option"
                  [attr.aria-selected]="c.iso2 === selectedCountry.iso2"
                  [class.is-active]="c.iso2 === selectedCountry.iso2"
                  (click)="selectCountry(c)"
                >
                  <span>{{ c.name }}</span>
                  <span>{{ c.dialCode }}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
        <input
          #numberInput
          id="pd-number"
          class="pd__input phone-row__number"
          type="tel"
          inputmode="tel"
          autocomplete="tel-national"
          placeholder="Mobile number"
          name="national"
          [(ngModel)]="national"
          (ngModelChange)="onNumberInput()"
          (blur)="touchedNumber = true"
          [attr.aria-invalid]="!!numberError"
          aria-describedby="pd-number-error"
        />
      </div>
      <p id="pd-number-error" class="pd__error" aria-live="polite">{{ numberError }}</p>
    </div>

    <label class="pd__consent">
      <input type="checkbox" name="consent" [(ngModel)]="consent" />
      <span>{{ consentText }}</span>
    </label>

    <div class="pd__alert" role="alert" *ngIf="error">{{ error.message }}</div>

    <div class="pd__actions">
      <button type="button" class="pd__btn pd__btn--ghost" *ngIf="allowSnooze" [disabled]="sending" (click)="snooze()">
        Remind me later
      </button>
      <button type="submit" class="pd__btn pd__btn--primary" [disabled]="!canSend">
        {{ sending ? 'Sending…' : 'Send code' }}
      </button>
    </div>
  </form>

  <!-- Step 2: code -->
  <div class="pd__body" *ngIf="step === 'code'">
    <div class="pd__field">
      <label id="pd-code-label" class="sr-only">6-digit code</label>
      <div class="code-row" role="group" aria-labelledby="pd-code-label">
        <input
          *ngFor="let i of codeSlots"
          #codeBox
          class="code-box"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="6"
          autocomplete="one-time-code"
          [attr.aria-label]="'Digit ' + (i + 1) + ' of 6'"
          [value]="digits[i]"
          [disabled]="codeDisabled"
          (input)="onDigitInput(i, $event)"
          (keydown)="onDigitKeydown(i, $event)"
          (paste)="onDigitPaste(i, $event)"
          (focus)="$any($event.target).select()"
        />
      </div>
    </div>

    <div class="pd__live" aria-live="polite">
      <p class="pd__alert" role="alert" *ngIf="error">{{ error.message }}</p>
      <p class="pd__hint" *ngIf="attemptsLeft !== null && !locked && !expired">
        {{ attemptsLeft }} {{ attemptsLeft === 1 ? 'attempt' : 'attempts' }} left
      </p>
    </div>

    <div class="pd__resend">
      <button
        type="button"
        class="pd__link"
        [disabled]="resendSeconds > 0 || resending || confirming"
        (click)="resend()"
      >
        {{ resending ? 'Sending…' : expired || locked ? 'Send a new code' : 'Resend code' }}
      </button>
      <span class="pd__hint" *ngIf="resendSeconds > 0" aria-hidden="true">in {{ resendSeconds }}s</span>
      <span class="sr-only" aria-live="polite">{{ countdownAnnouncement }}</span>
    </div>

    <div class="pd__actions">
      <button type="button" class="pd__btn pd__btn--ghost" [disabled]="busy" (click)="changeNumber()">
        Change number
      </button>
      <button
        type="button"
        class="pd__btn pd__btn--primary"
        [disabled]="code.length !== 6 || codeDisabled"
        (click)="confirm()"
      >
        {{ confirming ? 'Verifying…' : 'Verify' }}
      </button>
    </div>
  </div>

  <!-- Step 3: done -->
  <div class="pd__body pd__done" *ngIf="step === 'done'" aria-live="polite">
    <div class="pd__tick" aria-hidden="true"><i class="bi bi-check-lg"></i></div>
    <p class="pd__done-number">{{ doneMasked }}</p>
    <button type="button" class="pd__btn pd__btn--primary" data-autofocus
      (click)="cancel()">Done</button>
  </div>
</div>
